import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Plus, X, Edit2, Trash2 } from 'lucide-react';
import { getPositionGroups } from '../../data/positionHierarchy';
import type { Sport } from '../../types';
import { MobileSelect } from '../ui/MobileSelect';

export interface FormationPosition {
  id: string;
  label: string;
  fullName?: string;
  x: number; // Percentage from left (0-100)
  y: number; // Percentage from top (0-100)
  positionGroupKey?: string; // Required: maps to position group
  color?: string;
  isCenter?: boolean; // For Center position (square instead of circle)
}

interface VisualFormationBuilderProps {
  sport: Sport;
  unit: 'Offense' | 'Defense' | 'Special Teams';
  initialPositions?: FormationPosition[];
  onSave: (positions: FormationPosition[]) => void;
  onCancel: () => void;
  formationName?: string; // Kept for potential future use
}

// Default colors based on position group
const getDefaultColor = (positionGroupKey?: string): string => {
  if (positionGroupKey === 'QB') {
    return '#DC2626'; // Darker red for QB
  }
  if (positionGroupKey === 'OL') {
    return '#1E40AF'; // Darker blue for OL
  }
  return '#1E40AF'; // Default darker blue
};

// Auto-populate offensive line positions for Football
// Positions spaced with more room between each
const getDefaultOffensiveLine = (): FormationPosition[] => {
  return [
    { id: 'ol_lt', label: 'LT', fullName: 'Left Tackle', x: 35, y: 50, positionGroupKey: 'OL', color: '#1E40AF', isCenter: false },
    { id: 'ol_lg', label: 'LG', fullName: 'Left Guard', x: 42.5, y: 50, positionGroupKey: 'OL', color: '#1E40AF', isCenter: false },
    { id: 'ol_c', label: 'C', fullName: 'Center', x: 50, y: 50, positionGroupKey: 'OL', color: '#1E40AF', isCenter: true },
    { id: 'ol_rg', label: 'RG', fullName: 'Right Guard', x: 57.5, y: 50, positionGroupKey: 'OL', color: '#1E40AF', isCenter: false },
    { id: 'ol_rt', label: 'RT', fullName: 'Right Tackle', x: 65, y: 50, positionGroupKey: 'OL', color: '#1E40AF', isCenter: false },
  ];
};

const VisualFormationBuilder: React.FC<VisualFormationBuilderProps> = ({
  sport,
  unit,
  initialPositions = [],
  onSave,
  onCancel: _onCancel, // Unused but kept for interface compatibility
  formationName: _formationName // Unused but kept for interface compatibility
}) => {
  const [positions, setPositions] = useState<FormationPosition[]>(() => {
    // If no initial positions and this is Football Offense, auto-populate OL
    if (initialPositions.length === 0 && sport === 'Football' && unit === 'Offense') {
      return getDefaultOffensiveLine();
    }
    return initialPositions;
  });
  const [editingPosition, setEditingPosition] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [editingFullName, setEditingFullName] = useState('');
  const [editingPositionGroup, setEditingPositionGroup] = useState<string>('');
  const [isDragging, setIsDragging] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const positionGroups = getPositionGroups(sport, unit);
  const previousInitialPositionsRef = useRef<string>('');

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sync initialPositions to positions when they change (using ref to detect actual changes)
  useEffect(() => {
    const currentInitialStr = JSON.stringify(initialPositions);
    
    // Only update if initialPositions actually changed
    if (currentInitialStr !== previousInitialPositionsRef.current) {
      if (initialPositions.length > 0) {
        setPositions(initialPositions);
      } else if (initialPositions.length === 0 && sport === 'Football' && unit === 'Offense' && positions.length === 0) {
        // If no initial positions and this is Football Offense, auto-populate OL
        setPositions(getDefaultOffensiveLine());
      }
      previousInitialPositionsRef.current = currentInitialStr;
    }
  }, [initialPositions, sport, unit]); // Note: positions is intentionally not in deps

  // Sync positions with parent when they change
  useEffect(() => {
    onSave(positions);
  }, [positions, onSave]);

  // Color palette for position selection
  const colorPalette = [
    '#1E40AF', // Darker Blue (default)
    '#DC2626', // Darker Red (QB)
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#84CC16', // Lime
    '#F97316', // Orange
    '#6366F1', // Indigo
  ];

  // Snap grid size - different for horizontal (more granular) and vertical
  const SNAP_GRID_SIZE_HORIZONTAL = 1; // 1% for fine horizontal positioning (especially OL)
  const SNAP_GRID_SIZE_VERTICAL = 5; // 5% for vertical positioning

  // Snap to grid - different granularity for horizontal vs vertical
  const snapToGrid = useCallback((value: number, isHorizontal: boolean = true): number => {
    const gridSize = isHorizontal ? SNAP_GRID_SIZE_HORIZONTAL : SNAP_GRID_SIZE_VERTICAL;
    return Math.round(value / gridSize) * gridSize;
  }, []);

  // Handle field click to add new position (desktop/tablet only)
  const handleFieldClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (editingPosition || isDragging || isMobile) return;
    
    const field = fieldRef.current;
    if (!field) return;

    const rect = field.getBoundingClientRect();
    const rawX = ((e.clientX - rect.left) / rect.width) * 100;
    const rawY = ((e.clientY - rect.top) / rect.height) * 100;

    // Snap to grid - more granular horizontally
    const x = Math.max(5, Math.min(95, snapToGrid(rawX, true)));
    const y = Math.max(5, Math.min(95, snapToGrid(rawY, false)));

    const newPosition: FormationPosition = {
      id: `pos_${Date.now()}`,
      label: '', // Will be converted to uppercase when saved
      x,
      y,
      color: getDefaultColor(), // Default darker blue
      isCenter: false,
    };

    setPositions([...positions, newPosition]);
    setEditingPosition(newPosition.id);
    setEditingLabel('');
    setEditingFullName('');
    setEditingPositionGroup('');
  }, [positions, editingPosition, isDragging, isMobile, snapToGrid]);

  // Handle position drag start
  const handleDragStart = useCallback((e: React.MouseEvent, positionId: string) => {
    if (isMobile) return; // Disable drag on mobile
    e.stopPropagation();
    const position = positions.find(p => p.id === positionId);
    if (!position || !fieldRef.current) return;

    const positionElement = e.currentTarget as HTMLElement;
    const posRect = positionElement.getBoundingClientRect();
    
    const offsetX = e.clientX - (posRect.left + posRect.width / 2);
    const offsetY = e.clientY - (posRect.top + posRect.height / 2);

    setIsDragging(positionId);
    setDragOffset({ x: offsetX, y: offsetY });
  }, [positions, isMobile]);

  // Handle position drag
  const handleDrag = useCallback((e: MouseEvent) => {
    if (!isDragging || !fieldRef.current || isMobile) return;

    const field = fieldRef.current.getBoundingClientRect();
    const rawX = ((e.clientX - field.left - dragOffset.x) / field.width) * 100;
    const rawY = ((e.clientY - field.top - dragOffset.y) / field.height) * 100;

    // Snap to grid - more granular horizontally
    const x = Math.max(5, Math.min(95, snapToGrid(rawX, true)));
    const y = Math.max(5, Math.min(95, snapToGrid(rawY, false)));

    setPositions(prev => prev.map(p => 
      p.id === isDragging 
        ? { ...p, x, y }
        : p
    ));
  }, [isDragging, dragOffset, isMobile, snapToGrid]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(null);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  // Set up drag listeners
  useEffect(() => {
    if (isDragging && !isMobile) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDrag, handleDragEnd, isMobile]);

  // Save position edit
  const handleSavePosition = useCallback((positionId: string) => {
    if (!editingLabel.trim()) {
      alert('Please enter a position label');
      return;
    }

    if (!editingPositionGroup) {
      alert('Please select a position group. This is required for player filtering.');
      return;
    }

    setPositions(prev => {
      const updated = prev.map(p => {
        if (p.id === positionId) {
          const newPositionGroup = editingPositionGroup;
          // Update color based on position group if not already customized
          const shouldUpdateColor = !p.color || p.color === getDefaultColor(p.positionGroupKey);
          const newColor = shouldUpdateColor ? getDefaultColor(newPositionGroup) : p.color;
          
          // Always convert label to uppercase
          const labelUpper = editingLabel.trim().toUpperCase();
          
          const updatedPosition = {
            ...p,
            label: labelUpper,
            fullName: editingFullName.trim() || labelUpper,
            positionGroupKey: newPositionGroup,
            isCenter: labelUpper === 'C' || p.isCenter || false,
            color: newColor,
          };
          console.log('[VisualFormationBuilder] Updated position:', { id: updatedPosition.id, label: updatedPosition.label, positionGroupKey: updatedPosition.positionGroupKey });
          return updatedPosition;
        }
        return p;
      });
      // The onSave callback will be triggered by the useEffect that watches positions
      return updated;
    });
    setEditingPosition(null);
    setEditingLabel('');
    setEditingFullName('');
    setEditingPositionGroup('');
  }, [editingLabel, editingFullName, editingPositionGroup]);

  // Delete position
  const handleDeletePosition = useCallback((positionId: string) => {
    // Don't allow deleting OL positions if they're the default ones
    const position = positions.find(p => p.id === positionId);
    if (position?.positionGroupKey === 'OL' && position.id.startsWith('ol_')) {
      if (!confirm('This is a default offensive line position. Are you sure you want to delete it?')) {
        return;
      }
    }
    setPositions(prev => prev.filter(p => p.id !== positionId));
    if (editingPosition === positionId) {
      setEditingPosition(null);
    }
  }, [editingPosition, positions]);

  // Add new position (mobile-friendly)
  const handleAddPosition = useCallback(() => {
    const newPosition: FormationPosition = {
      id: `pos_${Date.now()}`,
      label: '', // Will be converted to uppercase when saved
      x: 50,
      y: 50,
      color: getDefaultColor(), // Default darker blue
      isCenter: false,
    };
    setPositions([...positions, newPosition]);
    setEditingPosition(newPosition.id);
    setEditingLabel('');
    setEditingFullName('');
    setEditingPositionGroup('');
  }, [positions]);

  // Get field background based on sport and unit
  const getFieldBackground = () => {
    if (sport === 'Football') {
      if (unit === 'Offense' || unit === 'Defense') {
        return 'bg-green-600';
      }
    }
    return 'bg-gray-200';
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg">
      <div className={`flex flex-col ${isMobile ? '' : 'sm:flex-row'} flex-1 overflow-hidden`}>
        {/* Field Canvas - Hidden on mobile, shown on tablet+ */}
        {!isMobile && (
          <div className="flex-1 p-3 sm:p-6 overflow-auto">
            <div className="relative max-w-4xl mx-auto">
              <div
                ref={fieldRef}
                className={`relative ${getFieldBackground()} rounded-lg shadow-inner cursor-crosshair`}
                style={{ 
                  aspectRatio: '16/9',
                  minHeight: '400px',
                  position: 'relative'
                }}
                onClick={handleFieldClick}
              >
                {/* Field markings - Horizontal yard lines (zoomed to line scale) */}
                {sport === 'Football' && (unit === 'Offense' || unit === 'Defense') && (
                  <>
                    {/* All yard line markers (horizontal) - 40 at top, 35, 30 middle, 25, 20 at bottom */}
                    {/* Evenly spaced: 20% (40), 35% (35), 50% (30), 65% (25), 80% (20) */}
                    {[
                      { yard: 40, position: 20, showNumber: true },
                      { yard: 35, position: 35, showNumber: false },
                      { yard: 30, position: 50, showNumber: true },
                      { yard: 25, position: 65, showNumber: false },
                      { yard: 20, position: 80, showNumber: true }
                    ].map(({ yard, position, showNumber }) => (
                      <div
                        key={yard}
                        className="absolute border-t border-white"
                        style={{ 
                          top: `${position}%`, 
                          left: 0, 
                          right: 0,
                          height: showNumber ? '2px' : '1px',
                          opacity: showNumber ? 0.5 : 0.3
                        }}
                      />
                    ))}
                    
                    {/* Large yard line numbers on left side - only show 40, 30, 20 */}
                    {[
                      { yard: 40, position: 20 },
                      { yard: 30, position: 50 },
                      { yard: 20, position: 80 }
                    ].map(({ yard, position }) => (
                      <div
                        key={`left-${yard}`}
                        className="absolute text-white font-bold opacity-90"
                        style={{
                          top: `${position}%`,
                          left: '3%',
                          transform: 'translateY(-50%)',
                          fontSize: '24px',
                          lineHeight: '1',
                        }}
                      >
                        {yard}
                      </div>
                    ))}
                    
                    {/* Large yard line numbers on right side - only show 40, 30, 20 */}
                    {[
                      { yard: 40, position: 20 },
                      { yard: 30, position: 50 },
                      { yard: 20, position: 80 }
                    ].map(({ yard, position }) => (
                      <div
                        key={`right-${yard}`}
                        className="absolute text-white font-bold opacity-90"
                        style={{
                          top: `${position}%`,
                          right: '3%',
                          transform: 'translateY(-50%)',
                          fontSize: '24px',
                          lineHeight: '1',
                        }}
                      >
                        {yard}
                      </div>
                    ))}
                  </>
                )}

                {/* Position markers */}
                {positions.map((position) => {
                  const isCenter = position.isCenter || position.label.toUpperCase() === 'C';
                  return (
                    <div
                      key={position.id}
                      className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-move ${
                        isDragging === position.id ? 'z-50' : 'z-10'
                      }`}
                      style={{
                        left: `${position.x}%`,
                        top: `${position.y}%`,
                      }}
                      onMouseDown={(e) => handleDragStart(e, position.id)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="relative group">
                        {/* Position marker - circle or square */}
                        <div
                          className={`${isCenter ? 'rounded' : 'rounded-full'} border-2 border-white shadow-lg flex items-center justify-center text-white font-bold text-sm`}
                          style={{
                            backgroundColor: position.color || getDefaultColor(position.positionGroupKey),
                            width: '60px',
                            height: '60px',
                          }}
                        >
                          {(position.label || '?').toUpperCase()}
                        </div>
                        
                        {/* Hover actions */}
                        <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingPosition(position.id);
                              setEditingLabel(position.label);
                              setEditingFullName(position.fullName || '');
                              setEditingPositionGroup(position.positionGroupKey || '');
                            }}
                            className="text-white hover:text-gray-200 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePosition(position.id);
                            }}
                            className="text-white hover:text-red-200 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Instructions overlay */}
                {positions.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-center text-white text-opacity-80">
                      <p className="text-lg font-semibold mb-2">Click anywhere to add a position</p>
                      <p className="text-sm">Drag positions to move them around</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sidebar/Mobile View - Position Editor */}
        <div className={`${isMobile ? 'w-full' : 'w-full sm:w-80'} border-t sm:border-l sm:border-t-0 bg-gray-50 p-4 overflow-y-auto`}>
          {editingPosition ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Edit Position</h3>
                <button
                  onClick={() => {
                    setEditingPosition(null);
                    setEditingLabel('');
                    setEditingFullName('');
                    setEditingPositionGroup('');
                  }}
                  className="btn btn-ghost btn-xs"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div>
                <label className="label">
                  <span className="label-text">Position Label *</span>
                </label>
                <input
                  type="text"
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value.toUpperCase())}
                  placeholder="e.g., DAWG, 2, 3, MIKE"
                  className="input input-bordered w-full uppercase"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is what you'll see on the field
                </p>
              </div>

              <div>
                <label className="label">
                  <span className="label-text">Full Name (Optional)</span>
                </label>
                <input
                  type="text"
                  value={editingFullName}
                  onChange={(e) => setEditingFullName(e.target.value)}
                  placeholder="e.g., Running Back, Wide Receiver"
                  className="input input-bordered w-full"
                />
              </div>

              <div>
                <label className="label">
                  <span className="label-text">Position Group *</span>
                </label>
                <MobileSelect
                  value={editingPositionGroup}
                  onChange={(v) => {
                    setEditingPositionGroup(v);
                    if (v) {
                      const newColor = getDefaultColor(v);
                      const currentPosition = positions.find(p => p.id === editingPosition);
                      if (currentPosition && (!currentPosition.color || currentPosition.color === getDefaultColor(currentPosition.positionGroupKey))) {
                        setPositions(prev => prev.map(p =>
                          p.id === editingPosition ? { ...p, color: newColor } : p
                        ));
                      }
                    }
                  }}
                  options={[
                    { value: '', label: 'Select position group...' },
                    ...positionGroups.map((group) => ({ value: group.key, label: group.name })),
                  ]}
                  className="select select-bordered w-full"
                  aria-label="Position Group"
                  sheetTitle="Position Group"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Required: Which position group can play this role? (e.g., RB, WR, OL)
                </p>
              </div>

              <div>
                <label className="label">
                  <span className="label-text">Position Color</span>
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {colorPalette.map((color) => {
                    const currentPosition = positions.find(p => p.id === editingPosition);
                    const isSelected = currentPosition?.color === color;
                    return (
                      <button
                        key={color}
                        type="button"
                        onClick={() => {
                          setPositions(prev => prev.map(p => 
                            p.id === editingPosition ? { ...p, color } : p
                          ));
                        }}
                        className={`w-full h-10 rounded border-2 transition-all ${
                          isSelected 
                            ? 'border-gray-800 scale-110 shadow-lg' 
                            : 'border-gray-300 hover:border-gray-500 hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Click a color to change the position marker color
                </p>
              </div>

              <button
                onClick={() => handleSavePosition(editingPosition)}
                className="btn btn-primary w-full"
              >
                Save Position
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Positions ({positions.length})</h3>
                {isMobile && (
                  <button
                    onClick={handleAddPosition}
                    className="btn btn-primary btn-sm flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Position
                  </button>
                )}
              </div>

              {positions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No positions added yet</p>
                  {!isMobile && (
                    <p className="text-sm mt-2">Click on the field to add positions</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {positions.map((position) => {
                    const isCenter = position.isCenter || position.label.toUpperCase() === 'C';
                    return (
                      <div
                        key={position.id}
                        className="flex items-center gap-2 p-2 bg-white rounded border cursor-pointer hover:bg-gray-50"
                        onClick={() => {
                          setEditingPosition(position.id);
                          setEditingLabel(position.label);
                          setEditingFullName(position.fullName || '');
                          setEditingPositionGroup(position.positionGroupKey || '');
                        }}
                      >
                        <div
                          className={`${isCenter ? 'rounded' : 'rounded-full'} flex-shrink-0`}
                          style={{ 
                            backgroundColor: position.color || getDefaultColor(position.positionGroupKey),
                            width: '24px',
                            height: '24px',
                          }}
                        />
                        <span className="flex-1 text-sm font-medium">
                          {(position.label || 'Unlabeled').toUpperCase()}
                        </span>
                        {position.positionGroupKey ? (
                          <span className="text-xs text-gray-500">
                            {positionGroups.find(g => g.key === position.positionGroupKey)?.name}
                          </span>
                        ) : (
                          <span className="text-xs text-red-500">⚠️ No mapping</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!isMobile && (
                <div className="mt-4">
                  <h3 className="font-semibold mb-2">Instructions</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Click on the field to add positions</li>
                    <li>• Drag positions to move them</li>
                    <li>• Click a position to edit it</li>
                    <li>• Map all positions to position groups (required)</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisualFormationBuilder;
